import styled from 'styled-components'
import moment from 'moment'
import { pick } from 'ramda'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { select } from '@/store'
import { Label } from '@/components/text'
import { Box } from '@/components/structure'
import { colors } from '@/styles'
import UserFullName from '@/components/user'
import { isNullOrEmpty } from '@/utils'
import { useBank } from '@/features/Pad/utils'
import { formatPhone } from '@/utils/format-phone'
import { getCountry } from '@/utils/address'
import useBasic from '@/hooks/use-basic'
import { LogoIcon } from '@/components/icons'
import type { ListingInfo, FlatPadInfo } from '@/features/Pad/types'
import { PaymentData } from '@/features/Payments/types'
import formatCurrency from '@/utils/format-currency'
import { WarningRow } from './elements'

const TermsWrapper = styled.div<Modal>`
  ${props =>
    props.isModal
      ? `
    overflow: hidden;
    border: 1px solid ${colors.grey88};
    border-radius: 6px;
    margin-bottom: 8px;
   `
      : `
    padding-bottom: 32px;
  `}
`

const TermsScrollContainer = styled.div<Modal>`
  ${props =>
    props.isModal &&
    `
    max-height: 352px;
    padding: 24px;
    overflow-y: auto;
  `}
`

const Table = styled.table`
  border-collapse: collapse;
  border: none;
  width: 100%;
  page-break-inside: avoid;
`

const Spacer = styled.span`
  display: block;
  height: 20px;
`

const TableHeading = styled.h3`
  font-weight: 600;
  line-height: 16px;
  color: ${colors.black};
  margin: 0 0 16px;
`

const Text = styled.div<{ theme: 'label' | 'value' }>`
  margin: 0;
  ${props =>
    props.theme === 'label'
      ? `
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${colors.grey56};

  `
      : `
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${colors.black};
  `}
`

const Cell = styled.td`
  border: 1px solid #dfe0e0;
  padding: 8px;
  vertical-align: top;
`

const Bold = styled.b`
  font-weight: 600;
`

const Wrapper = styled.div`
  overflow: visible;
  margin: 0;
  font-size: 14px;
  line-height: 20px;

  p,
  span {
    color: ${colors.black};
    margin: 0;
  }

  .mb24 {
    margin-bottom: 24px;
  }
`

const List = styled.ol`
  padding-left: 20px;
`

const Item = styled.li`
  list-style-type: decimal;
  margin-bottom: 12px;
  margin-right: 5px;
`

const SubList = styled.ol`
  padding-left: 0px;
  list-style-type: none;
`

const SubItem = styled.li`
  counter-increment: section;
  &:before {
    content: counter(section, lower-alpha) ') ';
  }
  padding: 5px 0;
`

const ListItemLabel = styled.span`
  padding-left: 20px;
`

const useCountry = (countryCode = '-'): string => {
  const { countries } = useBasic(['countries'])
  const country = getCountry(countryCode, countries)
  return country?.name ?? countryCode
}

type Modal = {
  isModal?: boolean
}

type ListingInfoProps = {
  listingInfo: ListingInfo
}

type FormValidation = {
  scrollRef?: any
  hasScrolledToBottom?: boolean
  showError?: boolean
}

type TermsProps = Modal &
  FormValidation &
  Partial<ListingInfoProps> & {
    values: FlatPadInfo
    paymentData: PaymentData
  }

type PriceInfoProps = {
  paymentData: PaymentData
}
const PriceInfo: React.FC<PriceInfoProps> = ({ paymentData }) => {
  return (
    <>
      <tr>
        <Cell colSpan={4}>
          <Text theme="label">Monthly, fixed payment of</Text>
          <Text theme="value">CAD {formatCurrency(paymentData?.charged)}</Text>
        </Cell>
      </tr>
      <tr>
        <Cell colSpan={4}>
          <Text theme="label">Payment Start Date</Text>
          <Text theme="value">{moment(paymentData?.due_date).format('MM-DD-YYYY')}</Text>
        </Cell>
      </tr>
      <tr>
        <Cell colSpan={4}>
          <Text theme="label">Frequency of Payment</Text>
          <Text theme="value">Monthly</Text>
        </Cell>
      </tr>
    </>
  )
}

const formatStreet = ({ street1, street2 }: { street1?: string; street2?: string }) =>
  [street1, street2].filter(el => !!el).join(' - ')

const hasInfo = <T extends {}>(data: T | undefined): data is T => !isNullOrEmpty(data)

// This is a legal document, English must be retained, no need to translate.
const Terms: React.FC<TermsProps> = ({
  values,
  isModal,
  paymentData,
  scrollRef,
  hasScrolledToBottom,
  showError = false,
  listingInfo
}) => {
  const { t } = useTranslation()
  const billingAddress = pick(['street1', 'street2'], values)
  const moneyAddress = pick(['bank_street1', 'bank_street2'], values)
  const headerAddress = hasInfo(billingAddress) && formatStreet(billingAddress)
  const bankAddress =
    hasInfo(moneyAddress) &&
    formatStreet({ street1: moneyAddress.bank_street1, street2: moneyAddress.bank_street2 })

  const bank = useBank(values?.custom_financial_institution || values?.financial_institution_name)
  const country = useCountry(values?.country)
  const bankCountry = useCountry('ca')
  const landlordId = (listingInfo as any)?.primary_ll_id

  const { users } = useSelector(state =>
    (select.listing.listingDetail as any)(state, {
      listingId: (listingInfo as any)?.listing_id
    })
  )
  const landlord = users?.[landlordId]
  return (
    <>
      <TermsWrapper isModal={isModal}>
        <TermsScrollContainer ref={scrollRef} isModal={isModal}>
          <Wrapper>
            <div>
              <LogoIcon />
              <Box top={24} bottom={24}>
                <Label type="large">Personal Pre-Authorized Debit Plan Terms & Conditions</Label>
              </Box>
              <div className="mb24">
                <TableHeading>Payor Information</TableHeading>
                <Table>
                  <tbody>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Payor Name(s)</Text>
                        <Text theme="value">
                          {!!(values?.first_name || values?.last_name) ? (
                            <UserFullName
                              firstName={values?.first_name}
                              lastName={values?.last_name}
                            />
                          ) : (
                            '-'
                          )}
                        </Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">
                          Payor Address (street address, PO Box, RR, apartment/building, floor,
                          suite)
                        </Text>
                        <Text theme="value">{headerAddress || '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Text theme="label">City/Town</Text>
                        <Text theme="value">{values.city ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Province/Territory/State</Text>
                        <Text theme="value">{values.state ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Postal Code</Text>
                        <Text theme="value">{values.zip ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Country</Text>
                        <Text theme="value">{country ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={2}>
                        <Text theme="label">Phone Number</Text>
                        <Text theme="value">{formatPhone(values.phone_number) ?? '-'}</Text>
                      </Cell>
                      <Cell colSpan={2}>
                        <Text theme="label">Email Address</Text>
                        <Text theme="value">{values.email ?? '-'}</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="mb24">
                <TableHeading>Payor Financial Institution/Banking Information</TableHeading>
                <Table>
                  <tbody>
                    <tr>
                      <Cell colSpan={1}>
                        <Text theme="label">Transit Number</Text>
                        <Text theme="value">{values.transit_number ?? '-'}</Text>
                      </Cell>
                      <Cell colSpan={1}>
                        <Text theme="label">Institution Number</Text>
                        <Text theme="value">{values.financial_institution_number ?? '-'}</Text>
                      </Cell>
                      <Cell colSpan={2}>
                        <Text theme="label">Account Number</Text>
                        <Text theme="value">{values.account_number ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Name of Financial Institution</Text>
                        <Text theme="value">{bank ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">
                          Branch Address (street address, PO Box, RR, apartment/building, Floor,
                          Suite)
                        </Text>
                        <Text theme="value">{bankAddress || '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Text theme="label">City/Town</Text>
                        <Text theme="value">{values.bank_city ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Province</Text>
                        <Text theme="value">{values.bank_state ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Postal Code</Text>
                        <Text theme="value">{values.bank_zip ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Country</Text>
                        <Text theme="value">{bankCountry}</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="mb24">
                <TableHeading>Payment Information</TableHeading>
                <Table>
                  <tbody>
                    <PriceInfo paymentData={paymentData} />
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Type of Pre-Authorized Debit</Text>
                        <Text theme="value">PERSONAL</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="mb24">
                <TableHeading>Payee Information</TableHeading>
                <Table>
                  <tbody>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Payee Name</Text>
                        <Text theme="value">Prompton Real Estate Services Inc.</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Payee Address</Text>
                        <Text theme="value">201-179 Davie Street</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Text theme="label">City/Town</Text>
                        <Text theme="value">Vancouver</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Province/Territory/State</Text>
                        <Text theme="value">British Columbia</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Postal Code</Text>
                        <Text theme="value">V6Z 2Y1</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Country</Text>
                        <Text theme="value">Canada</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={2}>
                        <Text theme="label">Phone Number</Text>
                        <Text theme="value">604-813-9730</Text>
                      </Cell>
                      <Cell colSpan={2}>
                        <Text theme="label">Email Address</Text>
                        <Text theme="value">pad-agreement@prompton.bc.ca</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mb24">
                <TableHeading>Landlord Information</TableHeading>
                <Table>
                  <tbody>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Landlord Name</Text>
                        <Text theme="value">{landlord?.billing_info?.name ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Landlord Address</Text>
                        <Text theme="value">{landlord?.billing_info?.address ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Text theme="label">City/Town</Text>
                        <Text theme="value">{landlord?.billing_info?.city ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Province/Territory/State</Text>
                        <Text theme="value">{landlord?.billing_info?.state ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Postal Code</Text>
                        <Text theme="value">{landlord?.billing_info?.zip ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Country</Text>
                        <Text theme="value">{landlord?.billing_info?.country ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell colSpan={2}>
                        <Text theme="label">Phone Number</Text>
                        <Text theme="value">
                          {formatPhone(landlord?.billing_info?.phone_number) ?? '-'}
                        </Text>
                      </Cell>
                      <Cell colSpan={2}>
                        <Text theme="label">Email Address</Text>
                        <Text theme="value">{landlord?.billing_info?.email ?? '-'}</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mb24">
                <TableHeading>Rental Property Information</TableHeading>
                <Table>
                  <tbody>
                    <tr>
                      <Cell colSpan={4}>
                        <Text theme="label">Rental Property Address </Text>
                        <Text theme="value">{listingInfo?.full_street_name ?? '-'}</Text>
                      </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Text theme="label">City/Town</Text>
                        <Text theme="value">{listingInfo?.city ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Province/Territory/State</Text>
                        <Text theme="value">{listingInfo?.state ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Postal Code</Text>
                        <Text theme="value">{listingInfo?.zip ?? '-'}</Text>
                      </Cell>
                      <Cell>
                        <Text theme="label">Country</Text>
                        <Text theme="value">{country ?? '-'}</Text>
                      </Cell>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div>
                <TableHeading className="mb24">
                  Personal Pre-Authorized Debit Plan Terms and Conditions
                </TableHeading>
                <List>
                  <Item>
                    <p>
                      In this Agreement , “<Bold>you</Bold>” or “<Bold>your</Bold>” refers to the
                      Payor stated on the above form of this Agreement, “<Bold>Prompton</Bold>
                      ”, “<Bold>us</Bold>”, “<Bold>we</Bold>”, and “<Bold>our</Bold>” refers to
                      Prompton Real Estate Services Inc., and “Landlord” refers to the Landlord
                      stated on the above form.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      <span>
                        You agree to Prompton debiting your account indicated on the previous form
                        (the {'"'}
                        <Bold>Account</Bold>
                        {'"'}) for personal/household or consumer purposes and you authorize
                        Prompton and any successor or assign of Prompton to draw a debit in paper,
                        electronic or other form, including any top-ups or adjustments, for the
                        purpose of making payment for consumer goods or services (a {'"'}
                        <Bold>Personal PAD</Bold>
                        {'"'}), on your Account at the financial institution indicated in this
                        Agreement (the {'"'}
                        <Bold>Financial Institution</Bold>
                        {'"'}) and you authorize the Financial Institution to honour and pay such
                        debits.
                      </span>
                      <Spacer aria-hidden />
                      <span>
                        This Agreement and your authorization are provided for the benefit of
                        Prompton and your Financial Institution and are provided in consideration of
                        your Financial Institution agreeing to process debits against your Account
                        in accordance with the Rules of the Canadian Payments Association. You agree
                        that any direction you may provide to draw a Personal PAD, and any Personal
                        PAD drawn in accordance with this Agreement, shall be binding on you as if
                        signed by you, and, in the case of paper debits, as if they were cheques
                        signed by you.
                      </span>
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You authorize Prompton, and the Financial Institution, to debit your Account
                      for regular recurring payments of the fixed payment amount stated on the
                      previous form at the set interval stated on the previous form.
                    </p>
                  </Item>
                  <Item>
                    <Bold>
                      <p>
                        If the amount that you are required to pay under the agreement with the
                        Payee changes, this authorization will continue to apply.
                      </p>
                    </Bold>
                  </Item>
                  <Item>
                    <p>
                      You may revoke authorization at any time, subject to providing 30 days prior
                      written notice to Prompton. This authority is to remain in effect until we
                      have received notification (outlined below) from you of its change or
                      termination. You may terminate this Personal PAD agreement at any time via
                      payment settings while logged into your liv.rent account and it will go into
                      effect within 30 days from the time this request is submitted.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      This authorization applies only to the method of payment and you agree that
                      cancellation of this authorization does not terminate or otherwise have any
                      effect on any contract that exists between you and any other person.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You understand that you may obtain a sample cancellation form, or further
                      information on your right to cancel a PAD Agreement, at your financial
                      institution or at www.payments.ca.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Prompton may terminate this Agreement at any time by providing you with
                      written notice.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Prompton has been contracted by the Landlord to collect rents and security
                      deposits owed by you under your tenancy agreement with the Landlord by issuing
                      PADs against your Account. You acknowledge that, in accordance with Prompton’s
                      agreement with the Landlord (the {'"'}
                      <Bold>Rent Collection Services Agreement</Bold>
                      {'"'}), Prompton will remit to the Landlord amounts collected from you under
                      this Agreement, less a service fee, and you authorize such activities.
                      Accordingly, Prompton is not the ultimate beneficiary of the amounts it debits
                      from your Account pursuant to this Agreement. For more information on the Rent
                      Collection Services Agreement, visit Liv.Rent or contact Prompton to request a
                      copy of Rent Collection Services Agreement. Termination or cancellation of
                      this Agreement does not affect your rights or obligations under your tenancy
                      agreement with the Landlord.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Upon the request of Prompton or Liv Strategies, you shall promptly provide
                      Prompton or Liv Strategies (as applicable) with the then-current version of
                      your tenancy agreement with the Landlord.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      ou agree that Prompton may disclose this Agreement and information about the
                      PADs issued against your Account to Liv Strategies Inc. ({'"'}
                      <Bold>Liv Strategies</Bold> {'"'}) and to the Landlord, including information
                      about the amount, frequency and fulfillment of such PADs. You also agree that
                      Liv Strategies may disclose this Agreement and information about the PADs
                      issued against your Account to Prompton and the Landlord, including
                      information about the amount, frequency and fulfillment of such PADs.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Prompton may disclose this Agreement to the "Processing Member" and the
                      "Sponsoring Member" as defined in Payments Canada Rule H1.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You agree that your Financial Institution is not required to verify that any
                      Personal PAD has been drawn in accordance with this Agreement, including the
                      amount, frequency and fulfillment of any purpose of any Personal PAD.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You agree that delivery of this Agreement to Prompton constitutes delivery by
                      you to your Financial Institution. You agree that the Prompton may deliver
                      this Agreement to our Payment Processors and agree to the disclosure of any
                      personal information which may be contained in this Agreement to such Payment
                      Processor.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      <Bold>
                        You agree to waive the confirmation requirement under Section 16(a) of
                        Payment’s Canada Rule H1.
                      </Bold>
                      You understand that if you did not waive this pre-notification requirements,
                      Prompton would be obligated to provide you with a Confirmation (as defined in
                      Payments Canada Rule H1) at least 10 calendar days before the due date of the
                      first PAD under this Agreement.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      <Bold>
                        You agree to waive the pre-notification requirement under Section 17 of
                        Payment’s Canada Rule H1.
                      </Bold>
                      You understand that if you did not waive this pre-notification requirements,
                      Prompton would be obligated, in respect of Personal PADs occurring at set
                      intervals, to provide you with notice of the amount to be debited and the due
                      date(s) of debiting, at least 10 calendar days before: (a) each and any change
                      in the amount of a fixed amount PAD and of any change to the payment dates;
                      and (b) the due date of every variable amount PAD after the first PAD.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You certify that all information provided with respect to the Account is
                      accurate and you agree to inform Prompton, in writing, of any change in the
                      Account information provided in this Agreement at least ten (10) business days
                      prior to the next due date of a Personal PAD. In the event of any such change,
                      this Agreement shall continue in respect of any new account to be used for
                      Personal PADs.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You understand that you have certain recourse rights if any debit does not
                      comply with this Agreement. For example, you have the right to receive
                      reimbursement for any debit that is not authorized or is not consistent with
                      this PAD Agreement. To obtain more information on your recourse rights,
                      contact your financial institution or visit www.payments.ca.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You represent, warrant, and guarantee that all persons whose signatures are
                      required to sign on the Account have agreed to this Agreement. In addition you
                      represent, warrant, and guarantee, where applicable, that you have the
                      authority to electronically agree to commit to this Agreement by secure
                      electronic signature and that my secure electronic signature conforms with the
                      requirements of Rule H1.
                    </p>
                  </Item>

                  <Item>
                    <p>You represent, warrant, and guarantee that: </p>
                    <SubList>
                      <SubItem>
                        <ListItemLabel>you are a resident of Canada; and </ListItemLabel>
                      </SubItem>
                      <SubItem>
                        <ListItemLabel>
                          the fixed-amount to be debited from your Account under this Agreement only
                          consists of rents and/or security deposits you owe the Landlord as a
                          renter of the Rental Property stated on the above form and as agreed upon
                          in your tenancy agreement with the Landlord; and
                        </ListItemLabel>
                      </SubItem>
                      <SubItem>
                        <ListItemLabel>
                          you are or intend to be the renter of the Rental Property stated on the
                          above form.
                        </ListItemLabel>
                      </SubItem>
                    </SubList>
                  </Item>

                  <Item>
                    <p>
                      You shall not use this Agreement to transfer: (a) funds to any person other
                      than the landlord of the real estate you are renting or intend to rent; and
                      (b) funds other than rents and security deposits owed by you to the Landlord
                      under your tenancy agreement with the Landlord.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Applicable to the Province of Quebec only: It is the express wish of the
                      parties that this Agreement and any related documents be drawn up and executed
                      in English. Les parties conviennent que la présente convention et tous les
                      documents s’y rattachant soient rédigés et signés en anglais.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      You agree to comply with the Rules of the Canadian Payments Association or any
                      other rules or regulations which may affect the services described herein, as
                      may be introduced in the future or are currently in effect and you agree to
                      execute any further documentation which may be prescribed from time to time by
                      the Canadian Payments Association in respect of the services described herein.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      This Agreement, and all matters arising out of or relating to this Agreement,
                      are governed by, and construed in accordance with, the laws of the province of
                      British Columbia, and the federal laws of Canada applicable therein. Each
                      party agrees to attorn to the non-exclusive jurisdiction of the courts of the
                      Province of British Columbia in relation to the enforcement of this Agreement.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      Liv Strategies is a third-party beneficiary of this Agreement with the right
                      to enforce it.
                    </p>
                  </Item>
                  <Item>
                    <p>
                      <span>
                        You acknowledge that this Agreement is being entered into as part of a beta
                        test of rent payment functionality to be facilitated through the Liv.Rent
                        platform. EXCEPT AS REQUIRED BY APPLICABLE LAW, NEITHER PROMPTON NOR LIV
                        STRATEGIES MAKE ANY REPRESENTATION, WARRANTY, OR GUARANTEE OF ANY KIND THAT
                        THE RENT PAYMENT SERVICE OR THE PADS TO BE ISSUED UNDER THIS AGREEMENT WILL
                        MEET YOUR REQUIREMENTS, OPERATE WITHOUT INTERRUPTION, ACHIEVE ANY INTENDED
                        RESULT, OR BE ERROR FREE. TO THE EXTENT PERMITTED UNDER APPLICABLE LAW, YOU
                        ASSUME ALL RISKS RELATED TO OR ARISING OUT OF OR RELATED TO YOU AGREEING TO
                        ENTER INTO THIS AGREEMENT. TO THE EXTENT PERMITTED UNDER APPLICABLE LAW, IN
                        NO EVENT WILL PROMPTON OR LIV STRATEGIES BE LIABLE UNDER OR IN CONNECTION
                        WITH THIS AGREEMENT OR ITS SUBJECT MATTER UNDER ANY LEGAL OR EQUITABLE
                        THEORY, INCLUDING BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT
                        LIABILITY AND OTHERWISE, FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT,
                        EXEMPLARY, SPECIAL, ENHANCED OR PUNITIVE DAMAGES, REGARDLESS OF WHETHER SUCH
                        PERSONS WERE ADVISED OF THE POSSIBILITY OF SUCH LOSSES OR DAMAGES OR SUCH
                        LOSSES OR DAMAGES WERE OTHERWISE FORESEEABLE, AND NOTWITHSTANDING THE
                        FAILURE OF ANY AGREED OR OTHER REMEDY OF ITS ESSENTIAL PURPOSE.
                      </span>
                      <Spacer aria-hidden />
                      <span>
                        IN NO EVENT WILL THE COLLECTIVE AGGREGATE LIABILITY OF PROMPTON AND LIV
                        STRATEGIES UNDER OR IN CONNECTION WITH THIS AGREEMENT OR ITS SUBJECT MATTER,
                        UNDER ANY LEGAL OR EQUITABLE THEORY, INCLUDING BREACH OF CONTRACT, TORT
                        (INCLUDING NEGLIGENCE), STRICT LIABILITY AND OTHERWISE, EXCEED THE GREATER
                        OF $100 AND THE AMOUNTS DEBITED AGAINST YOUR ACCOUNT PURSUANT TO THIS
                        AGREEMENT IN THE 12 MONTH PERIOD PRIOR TO THE EVENT GIVING RISE TO THE
                        CLAIM.
                      </span>
                    </p>
                  </Item>
                  <Item>
                    <p>You understand and agree to the foregoing terms and conditions.</p>
                  </Item>
                </List>
              </div>
            </div>
          </Wrapper>
        </TermsScrollContainer>
      </TermsWrapper>
      {!hasScrolledToBottom && showError && (
        <WarningRow marginTop={0} marginBottom={0}>
          {t(
            'b.payments.view.required_scroll_bottom.text',
            'Required: You must scroll to the bottom of the agreement before you can sign.'
          )}
        </WarningRow>
      )}
    </>
  )
}

export default Terms
